import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Receipt, History, Sparkles, ListChecks, CreditCard } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  invoiceGenGetSession,
  invoiceGenSelectInvoice,
  invoiceGenGetRecord,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";
import UploadStep from "./invoice-gen/UploadStep";
import SelectInvoiceStep from "./invoice-gen/SelectInvoiceStep";
import EditInvoiceStep from "./invoice-gen/EditInvoiceStep";
import PaymentSettingsDialog from "./invoice-gen/PaymentSettingsDialog";
import InvoiceAiChat from "./invoice-ai/InvoiceAiChat";

export default function InvoiceGenerator() {
  const nav = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("manual"); // 'manual' | 'ai'
  const [step, setStep] = useState("upload"); // 'upload' | 'select' | 'edit'
  const [sessionId, setSessionId] = useState(null);
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceNo, setSelectedInvoiceNo] = useState(null);
  const [draft, setDraft] = useState(null);
  const [originalDraft, setOriginalDraft] = useState(null);
  const [candidatePayments, setCandidatePayments] = useState([]);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [aiSeedMessage, setAiSeedMessage] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const consumedEditState = useRef(false);

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login", { replace: true }); return; }
    adminMe()
      .then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); },
  });

  useEffect(() => {
    const editRecordId = location.state?.editRecordId;
    if (!editRecordId || consumedEditState.current) return;
    consumedEditState.current = true;
    invoiceGenGetRecord(editRecordId)
      .then((res) => {
        setDraft(res.record);
        setOriginalDraft(res.record);
        setEditingRecordId(res.record.id);
        setMode("manual");
        setStep("edit");
        nav(location.pathname, { replace: true, state: {} });
      })
      .catch(() => toast.error("Could not load that invoice for editing."));
  }, [location, nav]);

  const handleUploaded = (res) => {
    setSessionId(res.session_id);
    setClient(res.client);
    setInvoices(res.invoices || []);
    setSelectedInvoiceNo(null);
    setStep("select");
  };

  const handleContinue = async () => {
    if (!selectedInvoiceNo) return;
    try {
      const res = await invoiceGenSelectInvoice(sessionId, selectedInvoiceNo);
      setDraft(res.draft);
      setOriginalDraft(res.draft);
      setCandidatePayments(res.candidate_payments || []);
      setStep("edit");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load that invoice.");
    }
  };

  const backToSelect = () => {
    if (editingRecordId) {
      setEditingRecordId(null);
      setDraft(null);
      setOriginalDraft(null);
      nav("/admin/invoice-history");
      return;
    }
    setStep("select");
  };
  const backToUpload = () => {
    setSessionId(null);
    setClient(null);
    setInvoices([]);
    setStep("upload");
  };

  const handleClosed = (target) => {
    setDraft(null);
    setOriginalDraft(null);
    setCandidatePayments([]);
    setSelectedInvoiceNo(null);
    if (target === "upload") backToUpload();
    else backToSelect();
  };

  const handleAskAI = (invoiceNumber) => {
    setAiSeedMessage(`I'd like to edit invoice ${invoiceNumber}.`);
    setMode("ai");
  };

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-10" data-testid="admin-invoice-generator-page">
      <SEO title="Invoice Generator" noIndex />
      <div className="flex items-center gap-3 mb-2">
        <Receipt className="text-[#3A7A8C]" size={22} />
        <h1 className="text-2xl font-bold text-[#080808]">Statement → Invoice Generator</h1>
        <div className="ml-auto flex items-center gap-4">
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            data-testid="invoice-gen-payment-settings-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#3A7A8C] hover:text-[#080808]"
          >
            <CreditCard size={14} /> Payment Details
          </button>
          <Link
            to="/admin/invoice-clients"
            data-testid="invoice-gen-view-clients-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#3A7A8C] hover:text-[#080808]"
          >
            <ListChecks size={14} /> Invoice Clients
          </Link>
          <Link
            to="/admin/invoice-history"
            data-testid="invoice-gen-view-history-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#3A7A8C] hover:text-[#080808]"
          >
            <History size={14} /> View Invoice History
          </Link>
        </div>
      </div>
      <p className="text-sm text-[#6B7280] mb-6">
        Internal tool for converting an off-site chair-massage transaction from a billing statement into
        an Ashlan-formatted invoice PDF.
      </p>

      <PaymentSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <div className="flex items-center gap-1 border-b border-[#E5E7EB] mb-8">
        <button
          type="button"
          onClick={() => setMode("manual")}
          data-testid="invoice-mode-manual-tab"
          className={
            mode === "manual"
              ? "px-4 py-2.5 text-sm font-semibold text-[#3A7A8C] border-b-2 border-[#5FA8B4] -mb-px"
              : "px-4 py-2.5 text-sm text-[#6B7280] hover:text-[#080808]"
          }
        >
          <Receipt size={14} className="inline mr-1.5 -mt-0.5" /> Manual Upload
        </button>
        <button
          type="button"
          onClick={() => setMode("ai")}
          data-testid="invoice-mode-ai-tab"
          className={
            mode === "ai"
              ? "px-4 py-2.5 text-sm font-semibold text-[#3A7A8C] border-b-2 border-[#5FA8B4] -mb-px"
              : "px-4 py-2.5 text-sm text-[#6B7280] hover:text-[#080808]"
          }
        >
          <Sparkles size={14} className="inline mr-1.5 -mt-0.5" /> AI Assistant
        </button>
      </div>

      {mode === "ai" && (
        <InvoiceAiChat seedMessage={aiSeedMessage} onSeedConsumed={() => setAiSeedMessage(null)} />
      )}

      {mode === "manual" && step === "upload" && <UploadStep onUploaded={handleUploaded} />}

      {mode === "manual" && step === "select" && (
        <SelectInvoiceStep
          client={client}
          invoices={invoices}
          selected={selectedInvoiceNo}
          onSelect={setSelectedInvoiceNo}
          onContinue={handleContinue}
          onBack={backToUpload}
        />
      )}

      {mode === "manual" && step === "edit" && draft && (
        <EditInvoiceStep
          draft={draft}
          setDraft={setDraft}
          originalDraft={originalDraft}
          candidatePayments={candidatePayments}
          editingRecordId={editingRecordId}
          onBack={backToSelect}
          onClosed={handleClosed}
          onAskAI={handleAskAI}
        />
      )}
    </main>
  );
}
